import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { parseInline } from '../lib/reviewMarkup'
import { clickable } from '../lib/a11y'

// ── ReviewBody ────────────────────────────────────────────────────────────────
// Renders a review body with markdown-lite applied (see lib/reviewMarkup). Shared by
// the composer preview, your own review card, and community reviews so a review reads
// identically everywhere. Inline ||spoilers|| blur individually and reveal on tap; the
// whole-body `contains_spoilers` flag blurs everything at once (a review that's spoiler-y
// start to finish).

function InlineSpoiler({ children }) {
  const [show, setShow] = useState(false)
  return (
    <span
      {...clickable((e) => { e?.stopPropagation?.(); setShow(s => !s) }, { label: show ? 'Hide spoiler' : 'Reveal spoiler' })}
      title={show ? 'Hide spoiler' : 'Tap to reveal spoiler'}
      style={{
        cursor: 'pointer', borderRadius: 4, padding: '0 3px',
        background: show ? 'rgba(var(--amber-rgb),0.14)' : 'rgba(var(--ink-rgb),0.16)',
        color: show ? 'inherit' : 'transparent',
        filter: show ? 'none' : 'blur(4px)',
        transition: 'filter 0.15s, color 0.15s, background 0.15s',
        userSelect: show ? 'auto' : 'none',
      }}
    >{children}</span>
  )
}

export function ReviewBody({ text, contains_spoilers = false, style = {} }) {
  const [revealed, setRevealed] = useState(false)
  const navigate = useNavigate()
  const hideAll = contains_spoilers && !revealed
  const parts = parseInline(String(text || ''))
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        color: 'var(--text)', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap',
        wordBreak: 'break-word', filter: hideAll ? 'blur(5px)' : 'none',
        userSelect: hideAll ? 'none' : 'auto', ...style,
      }}>
        {parts.map((p, i) => (
          p.t === 'b' ? <strong key={i}>{p.v}</strong>
            : p.t === 'i' ? <em key={i}>{p.v}</em>
              : p.t === 's' ? <InlineSpoiler key={i}>{p.v}</InlineSpoiler>
                : p.t === 'm' ? (
                  <span key={i} {...clickable((e) => { e?.stopPropagation?.(); navigate(`/u/${p.v}`) }, { label: `View @${p.v}` })}
                    style={{ color: 'var(--gold-leaf)', fontWeight: 600, cursor: 'pointer' }}>@{p.v}</span>
                )
                  : <span key={i}>{p.v}</span>
        ))}
      </div>
      {hideAll && (
        // stopPropagation: without it the reveal tap ALSO bubbles to an enclosing
        // clickable row (e.g. a Reading Room card), which opened the review overlay —
        // re-blurred — so it took two taps to actually read the spoiler (Jason 2026-07-23).
        <button onClick={(e) => { e.stopPropagation(); setRevealed(true) }} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--amber)', fontSize: 11, fontWeight: 700, fontFamily: 'var(--font)' }}>
          ⚠ Spoilers, tap to reveal
        </button>
      )}
    </div>
  )
}
